import { BigNumber, Container, Filters } from '../../../../components'
import { PeriodType } from '@indigohive/cogfy-types'
import { ConnectionsUsagesChart } from './components/ConnectionsUsagesChart'
import { DateSelect } from '../../../../components/DateSelect'
import { endOfMonth, startOfMonth } from 'date-fns'
import { FilterDrawer } from '../../../../components/FilterDrawer'
import { FilterDrawerButton } from '../../../../components/FilterDrawerButton'
import { formatCurrency } from './format-currency'
import { MAP_LOGO_BY_PROVIDER } from '../../../../components/__common/map-logo-by-provider'
import { ProviderName } from '@indigohive/cogfy-providers'
import { ProvidersUsagesChart } from './components/ProvidersUsagesChart'
import { useCogfy, useWorkspace, useTitle, useFilters } from '../../../../hooks'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterDrawerStack } from '../../../../components/FilterDrawerStack'
import { GetMostExpensiveConnectionQuery } from '@indigohive/cogfy-types/endpoints/getMostExpensiveConnection'

export function ConnectionsUsagesPage () {
  const cogfy = useCogfy()
  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!
  const getWorkspace = useWorkspace(workspace)

  const { t } = useTranslation()

  useTitle({
    title: t('Connections usage'),
    loading: getWorkspace.isLoading,
    error: getWorkspace.isError
  })

  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [periodType, setPeriodType] = useState<PeriodType>('today')
  const filtersHook = useFilters([
    { name: t('Collection'), valueLabel: t('All'), value: undefined },
    { name: t('Provider'), valueLabel: t('All'), value: undefined }
  ])

  const collection = filtersHook.filters.find(f => f.name === t('Collection'))
  const provider = filtersHook.filters.find(f => f.name === t('Provider'))

  const query: GetMostExpensiveConnectionQuery = {
    startDate: startDate || startOfMonth(new Date()),
    endDate: endDate || endOfMonth(new Date()),
    collectionId: collection?.value as string | undefined
  }

  const {
    data: mostExpensiveConnection,
    isLoading: isLoadingMostExpensiveConnection,
    isError: isErrorMostExpensiveConnection,
    refetch: refetchMostExpensiveConnection
  } = useQuery({
    queryKey: ['getMostExpensiveConnection', query],
    queryFn: ({ signal }) => cogfy.getMostExpensiveConnection(query, { signal })
  })

  const cost = mostExpensiveConnection
    ? formatCurrency(mostExpensiveConnection.currency as 'USD' | 'BRL' | 'EUR', mostExpensiveConnection.totalCosts)
    : 0

  return (
    <Container className="flex flex-col gap-4 !px-0">
      <div className='flex flex-row justify-end items-end gap-4 sticky top-0 z-10 border-b-2 bg-white py-3 px-2 mt-4'>
        <Filters filtersHook={filtersHook} />
        <FilterDrawerButton onClick={() => setOpen(true)} />
        <DateSelect
          className='!w-fit'
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          periodType={periodType}
          setPeriodType={setPeriodType}
          setOpen={setOpen}
        />
      </div>
      <FilterDrawer open={open} setOpen={setOpen} >
        <FilterDrawerStack
          filtersHook={filtersHook}
          renderCollection
          renderProvider
        />
      </FilterDrawer>

      <BigNumber.Card
        className="!w-fit mt-2"
        loading={isLoadingMostExpensiveConnection}
        error={isErrorMostExpensiveConnection}
        onRefetchClick={() => { refetchMostExpensiveConnection() }}
      >
        {t('workspaceUsagePage:Highest cost in the selected period')}
        <span className="text-lg font-semibold">{mostExpensiveConnection?.connectionName}</span>
        <BigNumber.Number>
          <div className="flex items-center gap-2">
            {MAP_LOGO_BY_PROVIDER[mostExpensiveConnection?.provider as ProviderName] && (
            <img
              src={MAP_LOGO_BY_PROVIDER[mostExpensiveConnection?.provider as ProviderName]}
              alt={`${mostExpensiveConnection?.provider}-logo`}
              className="size-9 rounded-md"
            />
            )}
            {cost || '-'}
          </div>
        </BigNumber.Number>
      </BigNumber.Card>
      <ConnectionsUsagesChart
        collectionId={collection?.value as string | undefined}
        provider={provider?.value as string | undefined}
        endDate={endDate}
        startDate={startDate}
      />
      <ProvidersUsagesChart
        connectionOwner={['record', 'customer']}
        endDate={endDate}
        startDate={startDate}
      />
    </Container>
  )
}
